import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Shield, Leaf, Star, Flame, MapPin } from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { API, useAuth } from "../App";
import { motion } from "framer-motion";

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { login, user } = useAuth();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const features = [
    {
      icon: Leaf,
      title: "Premium Quality",
      description: "Carefully selected chilli powder crafted for rich colour and bold flavour.",
    },
    {
      icon: Shield,
      title: "Pure & Fresh",
      description: "Freshly packed with care to preserve aroma, taste, and quality.",
    },
    {
      icon: Truck,
      title: "Fast Delivery",
      description: "Quick dispatch and free shipping on orders above ₹500.",
    },
  ];

  const highlights = [
    { icon: Flame, text: "Bold Heat" },
    { icon: MapPin, text: "Guntur Inspired" },
    { icon: Shield, text: "Freshly Packed" },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="home-page">
      <Header />

      {/* Top Trust Strip */}
      <section className="border-y border-red-100 bg-gradient-to-r from-[#fff7f6] via-white to-[#fff3f1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-700">
            <span>Free Shipping above ₹500</span>
            <span className="hidden sm:inline text-red-400">•</span>
            <span>Premium Quality</span>
            <span className="hidden sm:inline text-red-400">•</span>
            <span>Freshly Packed</span>
            <span className="hidden sm:inline text-red-400">•</span>
            <span>Authentic Spice Experience</span>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="/images/hero-chilli.jpg"
            alt="Red chillies and spice background"
            className="w-full h-full object-cover scale-[1.03]"
          />

          {/* lighter spice-red cinematic overlay so image stays visible */}
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(20,6,6,0.62)_0%,rgba(55,10,10,0.48)_30%,rgba(110,18,18,0.28)_62%,rgba(165,28,28,0.12)_100%)]"></div>

          {/* soft atmosphere */}
          <div className="absolute -left-20 top-10 h-[420px] w-[420px] rounded-full bg-red-700/20 blur-3xl"></div>
          <div className="absolute left-[18%] bottom-[-60px] h-[280px] w-[280px] rounded-full bg-orange-500/12 blur-3xl"></div>
          <div className="absolute inset-0 shadow-[inset_0_0_140px_rgba(0,0,0,0.18)]"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/25 text-red-100 text-sm font-medium mb-6 border border-white/10 backdrop-blur-sm">
              <Flame className="w-4 h-4 text-red-300" />
              Premium Traditional Spice Blend
            </span>

            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-6 drop-shadow-[0_6px_24px_rgba(0,0,0,0.45)]">
              BT Premium{" "}
              <span className="bg-[linear-gradient(135deg,#ef4444_0%,#dc2626_35%,#fb923c_100%)] bg-clip-text text-transparent">
                Mirchi Powder
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-white/90 mb-8 leading-relaxed max-w-2xl drop-shadow-[0_4px_14px_rgba(0,0,0,0.35)]">
              Rich colour. Bold heat. Authentic flavour. Premium chilli powder inspired by the spice
              tradition of Guntur, made for kitchens that love real taste.
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              {highlights.map((item) => (
                <div
                  key={item.text}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/20 border border-white/10 text-sm text-white backdrop-blur-sm"
                >
                  <item.icon className="w-4 h-4 text-red-300" />
                  {item.text}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <Link to="/products">
                <Button
                  className="bg-[linear-gradient(135deg,#b91c1c_0%,#dc2626_55%,#ef4444_100%)] hover:opacity-95 text-white rounded-full px-8 py-6 text-base shadow-[0_14px_30px_rgba(220,38,38,0.35)]"
                  data-testid="shop-now-btn"
                >
                  Shop Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>

              {!user && (
                <Button
                  variant="outline"
                  onClick={login}
                  className="border-white/25 bg-white/5 text-white hover:bg-white/10 rounded-full px-8 py-6 text-base"
                  data-testid="hero-signin-btn"
                >
                  Sign In with Google
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-red-600 text-sm font-medium uppercase tracking-wider">
              Why Choose BT Premium
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2">
              Spice That Feels Premium
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -6 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="rounded-3xl bg-white border border-red-100 shadow-[0_14px_35px_rgba(0,0,0,0.06)] p-6 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mb-4 shadow-sm">
                  <feature.icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-[#fff8f7]" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="text-red-600 text-sm font-medium uppercase tracking-wider">
                Our Collection
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2">
                Premium Mirchi Powder
              </h2>
              <p className="text-gray-600 mt-3 max-w-xl">
                Choose the ideal pack size for your kitchen, from everyday cooking to larger family use.
              </p>
            </div>
            <Link to="/products" className="mt-6 md:mt-0">
              <Button
                variant="outline"
                className="rounded-full border-red-200 hover:bg-white text-gray-900"
                data-testid="view-all-btn"
              >
                View All Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-3xl aspect-square animate-pulse bg-white border border-red-100 shadow-sm"
                ></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product, index) => (
                <ProductCard key={product.product_id} product={product} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="group relative rounded-[2rem] overflow-hidden border border-red-100 shadow-[0_20px_55px_rgba(0,0,0,0.10)]">
              <img
                src="/images/story-chilli.jpg"
                alt="Traditional spices"
                className="w-full h-[420px] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-red-900/18 via-transparent to-orange-400/12"></div>
            </div>

            <div>
              <span className="text-red-600 text-sm font-medium uppercase tracking-wider">
                Our Promise
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2 mb-5">
                Bold Flavor For Every Kitchen
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                BT Premium Chilli Store is built around one simple promise: deliver powerful flavour,
                rich colour, and premium quality in every pack.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Whether you cook traditional Indian recipes or modern dishes, our mirchi powder is made
                to bring authentic spice to every meal.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-red-100 p-4 bg-white shadow-[0_12px_30px_rgba(0,0,0,0.05)]">
                  <p className="text-2xl font-bold text-red-600 mb-1">100%</p>
                  <p className="text-sm text-gray-600">Focused on premium spice quality</p>
                </div>
                <div className="rounded-2xl border border-red-100 p-4 bg-white shadow-[0_12px_30px_rgba(0,0,0,0.05)]">
                  <p className="text-2xl font-bold text-red-600 mb-1">3</p>
                  <p className="text-sm text-gray-600">Carefully selected pack sizes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bulk Orders + Discount */}
      <section className="py-16 bg-[#fff8f7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-3xl border border-red-100 p-8 shadow-[0_12px_35px_rgba(0,0,0,0.05)] bg-white">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Bulk Orders
              </h3>

              <p className="text-gray-600 mb-6">
                Need larger quantities? Contact us for wholesale pricing and custom pack sizes.
              </p>

              <Button className="rounded-full bg-white text-gray-900 border border-red-200 hover:bg-red-50 shadow-sm">
                Contact for Bulk Orders
              </Button>
            </div>

            <div className="relative overflow-hidden rounded-[2rem] border border-red-400/40 bg-[linear-gradient(135deg,#7f1d1d_0%,#b91c1c_55%,#dc2626_100%)] p-8 shadow-[0_22px_55px_rgba(127,29,29,0.30)]">
              <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl"></div>
              <div className="absolute bottom-[-30px] left-[-20px] h-32 w-32 rounded-full bg-orange-300/15 blur-2xl"></div>

              <h3 className="relative text-2xl font-bold text-white mb-4">
                First Order Discount
              </h3>

              <p className="relative text-red-100 text-lg mb-4">
                Use code <span className="font-bold text-white">WELCOME10</span> at checkout for 10% off your first order.
              </p>

              <p className="relative text-red-200 text-sm">
                *Minimum order value ₹200
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-red-600 text-sm font-medium uppercase tracking-wider">
              Testimonials
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Priya Sharma",
                location: "Delhi",
                text: "The colour and aroma are excellent. It instantly lifts the flavour of my curries.",
                rating: 5,
              },
              {
                name: "Rajesh Kumar",
                location: "Mumbai",
                text: "A premium feel with authentic taste. This is now my regular chilli powder.",
                rating: 5,
              },
              {
                name: "Anitha Reddy",
                location: "Bangalore",
                text: "Rich flavour, fast delivery, and quality packaging. Very happy with the purchase.",
                rating: 5,
              },
            ].map((review, index) => (
              <motion.div
                key={review.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -6 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="rounded-3xl bg-white border border-red-100 shadow-[0_14px_35px_rgba(0,0,0,0.06)] p-6"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-red-500 text-red-500" />
                  ))}
                </div>
                <p className="text-gray-600 text-sm mb-4">"{review.text}"</p>
                <div>
                  <p className="text-sm font-medium text-gray-900">{review.name}</p>
                  <p className="text-xs text-gray-500">{review.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#fff8f7]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[2rem] overflow-hidden border border-red-100 shadow-[0_25px_65px_rgba(0,0,0,0.12)]">
            <img
              src="/images/cta-chilli.jpg"
              alt="Cooking with premium spices"
              className="w-full h-80 object-cover scale-[1.03]"
            />

            <div className="absolute inset-0 bg-gradient-to-r from-[rgba(20,6,6,0.72)] via-[rgba(75,12,12,0.48)] to-[rgba(185,28,28,0.18)] flex items-center">
              <div className="px-8 md:px-12 max-w-2xl">
                <h3 className="text-2xl md:text-4xl font-bold text-white mb-4 drop-shadow-[0_4px_16px_rgba(0,0,0,0.35)]">
                  Bring Home The Taste of{" "}
                  <span className="bg-[linear-gradient(135deg,#ef4444_0%,#fb923c_100%)] bg-clip-text text-transparent">
                    Premium Spice Tradition
                  </span>
                </h3>
                <p className="text-gray-100 mb-6 max-w-xl">
                  Start with our most loved packs and bring authentic heat, rich colour, and bold flavour
                  to every meal.
                </p>
                <Link to="/products">
                  <Button
                    className="bg-[linear-gradient(135deg,#b91c1c_0%,#dc2626_55%,#ef4444_100%)] hover:opacity-95 text-white rounded-full px-8 shadow-[0_14px_30px_rgba(220,38,38,0.35)]"
                    data-testid="cta-shop-btn"
                  >
                    Start Shopping
                  </Button>
                </Link>
              </div>
            </div>

            <div className="absolute -left-10 bottom-[-30px] h-40 w-40 rounded-full bg-red-600/20 blur-3xl"></div>
            <div className="absolute right-12 top-8 h-24 w-24 rounded-full bg-orange-300/16 blur-2xl"></div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;